import React from 'react';
import { Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import {
    DECISION_GUIDE_INTAKE_ERROR_BODY,
    DECISION_GUIDE_INTAKE_ERROR_TITLE,
    DECISION_GUIDE_INTAKE_LOADING_BODY,
    DECISION_GUIDE_INTAKE_LOADING_TITLE,
    DECISION_GUIDE_INTAKE_ROW_INCONSISTENT,
} from './decisionGuideCopy';

export type IntakeGuidancePreviewState = 'loading' | 'ready' | 'error';

export interface IntakeDecisionGuideCardProps {
    state: IntakeGuidancePreviewState;
    calmSummary?: string | null;
    safeFirstStep?: string | null;
    workItemId?: string | null;
    onReviewOptions?: () => void;
    onRetry?: () => void;
}

/**
 * List-backed orientation preview for Documents intake (no detail fetch).
 * States: loading (list in flight), ready (server-enriched fields), error (list/row unusable).
 */
const IntakeDecisionGuideCard: React.FC<IntakeDecisionGuideCardProps> = ({
    state,
    calmSummary,
    safeFirstStep,
    workItemId,
    onReviewOptions,
    onRetry,
}) => {
    if (state === 'loading') {
        return (
            <Card
                variant="outlined"
                sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'rgba(99, 102, 241, 0.03)',
                    borderColor: 'rgba(99, 102, 241, 0.15)',
                    borderRadius: 2,
                }}
                role="status"
                aria-live="polite"
                aria-busy="true"
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <CircularProgress size={22} sx={{ color: 'var(--color-accent)', mt: 0.25 }} aria-hidden />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                            {DECISION_GUIDE_INTAKE_LOADING_TITLE}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', mt: 0.5 }}>
                            {DECISION_GUIDE_INTAKE_LOADING_BODY}
                        </Typography>
                    </Box>
                </Box>
            </Card>
        );
    }

    if (state === 'error') {
        return (
            <Card
                variant="outlined"
                sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'rgba(148, 163, 184, 0.06)',
                    borderColor: 'rgba(148, 163, 184, 0.25)',
                    borderRadius: 2,
                }}
                role="alert"
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'rgba(148, 163, 184, 0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                        aria-hidden
                    >
                        <FontAwesomeIcon icon={faLightbulb} style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                            {DECISION_GUIDE_INTAKE_ERROR_TITLE}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', mt: 0.5 }}>
                            {DECISION_GUIDE_INTAKE_ERROR_BODY}
                        </Typography>
                        {onRetry && (
                            <Button
                                size="small"
                                variant="text"
                                onClick={onRetry}
                                sx={{ mt: 1, textTransform: 'none', px: 0 }}
                                startIcon={<FontAwesomeIcon icon={faRotateRight} style={{ fontSize: '0.75rem' }} />}
                            >
                                Retry
                            </Button>
                        )}
                    </Box>
                </Box>
            </Card>
        );
    }

    const summary = (calmSummary || '').trim();
    if (state === 'ready' && !summary) {
        return (
            <Card
                variant="outlined"
                sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'rgba(148, 163, 184, 0.06)',
                    borderColor: 'rgba(148, 163, 184, 0.2)',
                    borderRadius: 2,
                }}
                role="status"
            >
                <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                    {DECISION_GUIDE_INTAKE_ROW_INCONSISTENT}
                </Typography>
                {onRetry && (
                    <Button
                        size="small"
                        variant="text"
                        onClick={onRetry}
                        sx={{ mt: 1, textTransform: 'none', px: 0 }}
                        startIcon={<FontAwesomeIcon icon={faRotateRight} style={{ fontSize: '0.75rem' }} />}
                    >
                        Refresh list
                    </Button>
                )}
            </Card>
        );
    }

    if (!summary) {
        return null;
    }

    return (
        <Card
            variant="outlined"
            sx={{
                mt: 2,
                p: 2,
                bgcolor: 'rgba(99, 102, 241, 0.03)',
                borderColor: 'rgba(99, 102, 241, 0.15)',
                borderRadius: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: 'rgba(99, 102, 241, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                    aria-hidden
                >
                    <FontAwesomeIcon icon={faLightbulb} style={{ color: 'var(--color-accent)', fontSize: '0.9rem' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, color: 'var(--color-text-primary)', mb: 0.5 }}>
                        Orientation summary
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-text-primary)', mb: 0.5 }}>
                        {summary}
                    </Typography>
                    {safeFirstStep && (
                        <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', display: 'block' }}>
                            <strong>Safe first step:</strong> {safeFirstStep}
                        </Typography>
                    )}
                    {workItemId && onReviewOptions && (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={onReviewOptions}
                            sx={{
                                mt: 1.5,
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                bgcolor: 'var(--color-accent)',
                                '&:hover': { bgcolor: 'var(--color-accent)', opacity: 0.9 },
                            }}
                        >
                            Review your options
                        </Button>
                    )}
                </Box>
            </Box>
        </Card>
    );
};

export default IntakeDecisionGuideCard;
